import { useOnClickOutside } from '@/hooks';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import { DropdownActionBarProps } from '@/storybook/components/FormCollection/components/DropdownActionBar/DropdownActionBar';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownLabel from '@/storybook/components/FormCollection/components/DropdownLabel/DropdownLabel';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import {
  DropdownPosition,
  createSelectItemKey,
  createStableObjectHash,
  extractSelectItemLabelText,
  getSelectItemLabelContent,
  handleDropdownListPosition,
  isOptionSelected,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import cx from 'classnames';
import { JSX, ReactNode, useCallback, useMemo, useState } from 'react';
import styles from '../Input.module.css';

export type SingleSelectProps<T = string> = {
  'label'?: ReactNode;
  'tooltip'?: ReactNode;
  'options': SelectItem<T>[];
  'isLoading'?: boolean;
  'value'?: T;
  'onChange': (selectedValue?: T) => void;
  'onClear'?: VoidFunction;
  'placeholder'?: string | JSX.Element;
  'disabled'?: boolean;
  'clearable'?: boolean;
  'adobeEventTrackFunctions'?: VoidFunction;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownLabelTestId'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'customSelectItemFn'?: (value: T, options: SelectItem<T>[]) => SelectItem<T> | undefined;
  'size'?: keyof typeof DropdownButtonSize;
  'className'?: string;
  'dropdownButtonClassName'?: string;
  'dropdownIndicator'?: ReactNode;
  'actionBarProps'?: DropdownActionBarProps;
};

export default function SingleSelect<T>({
  label,
  tooltip,
  options,
  isLoading = false,
  value,
  onChange,
  onClear,
  placeholder,
  disabled = false,
  clearable = true,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'singleSelectDropdown',
  dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  customSelectItemFn,
  size = 'L',
  className,
  dropdownButtonClassName,
  dropdownIndicator,
  actionBarProps,
}: SingleSelectProps<T>) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');

  const fixedValue = useMemo(() => {
    return value != null ? [value] : [];
  }, [value]);

  const fixedOptions = useMemo(() => {
    if (options == null) return [];
    return options.filter((opt): opt is SelectItem<T> => opt != null && opt.value != null && opt.label != null);
  }, [options]);

  const fixedOptionsLength = fixedOptions.length;

  const dropdownRef = useOnClickOutside(() => setIsOpen(false));

  useScrollIntoView({
    elementRef: dropdownRef,
    isVisible: isOpen,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => !prev);
    onDropdownToggle?.(!isOpen);
    handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
  }, [disabled, dropdownPosition, dropdownRef, isOpen, onDropdownToggle]);

  const handleOnClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    if (onClear) onClear();
    if (!onClear) onChange();
  }, [onClear, disabled, clearable, onChange]);

  const handleClickOption = useCallback(
    (selectedValue: T) => {
      if (disabled) return;
      onChange(selectedValue);
      setIsOpen(prev => !prev);
    },
    [onChange, disabled],
  );

  const dropdownButtonValue = useMemo(() => {
    if (fixedValue.length === 0) return [];
    return fixedValue
      .map(currentValue => {
        if (customSelectItemFn) {
          return currentValue ? customSelectItemFn(fixedValue[0], fixedOptions) : undefined;
        }
        const currentValueKey =
          typeof currentValue === 'object' && currentValue !== null
            ? createStableObjectHash(currentValue)
            : String(currentValue);

        return fixedOptions.find(availableOption => {
          const availableValueKey =
            typeof availableOption.value === 'object' && availableOption.value !== null
              ? createStableObjectHash(availableOption.value)
              : String(availableOption.value);
          return availableValueKey === currentValueKey;
        });
      })
      .filter(selectItem => selectItem != null);
  }, [fixedOptions, fixedValue, customSelectItemFn]);

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.inputContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
    >
      <DropdownLabel label={label} tooltip={tooltip} data-testid={dropdownLabelTestId} />
      <section className={styles.dropdownContainer}>
        <DropdownButton
          type="singleSelect"
          size={size}
          className={dropdownButtonClassName}
          dropdownIndicator={dropdownIndicator}
          placeholder={placeholder}
          numOfValue={dropdownButtonValue.length}
          active={isOpen}
          onClick={!disabled ? handleToggleDropdown : undefined}
          onClear={handleOnClearValue}
          clearable={clearable}
          disabled={disabled}
          data-testid={dropdownButtonTestId}
        >
          <div className={styles.value} data-testid="value-singleSelect-list">
            <div>{dropdownButtonValue[0]?.label}</div>
            <span>{dropdownButtonValue[0]?.additionalInfo}</span>
          </div>
        </DropdownButton>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          data-testid={dropdownListTestId}
          numberOfSearchResults={fixedOptionsLength}
          optionsCount={fixedOptionsLength}
          actionBarProps={actionBarProps}
        >
          {fixedOptions.map(option => {
            if (option == null) {
              return null;
            }
            const optionKey = createSelectItemKey(option);
            const labelText = extractSelectItemLabelText(option);
            const labelContent = getSelectItemLabelContent(option);
            return (
              <li key={optionKey} className={styles.dropdownOptionListItem}>
                <TreeItem
                  label={labelText}
                  labelContent={labelContent}
                  variant="singleSelect"
                  level={0}
                  isSelected={!!isOptionSelected(option, fixedValue)}
                  isDisabled={disabled}
                  onClick={() => handleClickOption(option.value)}
                  endContent={option.additionalInfo}
                  data-testid={`singleSelectOption-${optionKey}`}
                />
              </li>
            );
          })}
        </DropdownList>
      </section>
    </div>
  );
}
